import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  getWricefByPackage,
  getWricefCatalog,
  getWricefCatalogSummary,
  getWricefComponents,
  getWricefObject,
  getWricefWorklist,
} from "@/api/wricef-catalog";
import type { WricefCatalogQuery, WricefWorklistQuery } from "@/types/wricef";

export function useWricefCatalog(
  projectId: string | undefined,
  query: WricefCatalogQuery = {},
  options: { keepPreviousPage?: boolean } = {},
) {
  return useQuery({
    queryKey: ["wricef-catalog", projectId, query],
    queryFn: () => getWricefCatalog(projectId as string, query),
    enabled: Boolean(projectId),
    staleTime: 30_000,
    // Na paginação, a página anterior fica na tela enquanto a nova chega, em
    // vez de a tabela virar esqueleto a cada clique em Next.
    ...(options.keepPreviousPage ? { placeholderData: keepPreviousData } : {}),
  });
}

export function useWricefWorklist(
  projectId: string | undefined,
  query: WricefWorklistQuery = {},
) {
  return useQuery({
    // Mesmo prefixo da lista: o painel da varredura invalida `["wricef-catalog",
    // projectId]` ao terminar, e a worklist precisa acompanhar.
    queryKey: ["wricef-catalog", projectId, "worklist", query],
    queryFn: () => getWricefWorklist(projectId as string, query),
    enabled: Boolean(projectId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useWricefCatalogSummary(
  projectId: string | undefined,
  scanJobId?: string | null,
) {
  return useQuery({
    queryKey: ["wricef-catalog-summary", projectId, scanJobId ?? null],
    queryFn: () => getWricefCatalogSummary(projectId as string, scanJobId),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });
}

export function useWricefByPackage(
  projectId: string | undefined,
  scanJobId?: string | null,
) {
  return useQuery({
    queryKey: ["wricef-by-package", projectId, scanJobId ?? null],
    queryFn: () => getWricefByPackage(projectId as string, scanJobId),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });
}

export function useWricefObject(
  projectId: string | undefined,
  objName: string | undefined,
) {
  return useQuery({
    queryKey: ["wricef-object", projectId, objName],
    queryFn: () => getWricefObject(projectId as string, objName as string),
    enabled: Boolean(projectId && objName),
    staleTime: 30_000,
  });
}

/** As peças do executável, buscadas quando o detalhe dele abre (#1083). */
export function useWricefComponents(
  projectId: string | undefined,
  objName: string | undefined,
) {
  return useQuery({
    // Mesmo prefixo da lista: a varredura que termina invalida os dois juntos.
    queryKey: ["wricef-catalog", projectId, "components", objName],
    queryFn: () => getWricefComponents(projectId as string, objName as string),
    enabled: Boolean(projectId && objName),
    staleTime: 30_000,
  });
}
